import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { API } from "@/App";
import { toast } from "sonner";

import { 
  ArrowLeft, ArrowRight, CheckCircle, Loader2, 
  Building2, Target, Clock, DollarSign, FileText, Users,
  Sparkles, Mail, Phone, Briefcase, Calendar
} from "lucide-react";

const PROJECT_TYPES = [
  { id: "business_setup", label: "Business Setup & Formation", description: "LLC, EIN, registrations" },
  { id: "brand_design", label: "Brand & Graphic Design", description: "Logo, brand identity, marketing materials" },
  { id: "sop_creation", label: "SOP & Process Documentation", description: "Standard operating procedures, workflows" },
  { id: "website", label: "Website Development", description: "New site, redesign, landing pages" },
  { id: "marketing", label: "Marketing Strategy", description: "Campaigns, funnels, content strategy" },
  { id: "social_media", label: "Social Media Management", description: "Content, scheduling, engagement" },
  { id: "seo", label: "SEO & Content", description: "Search optimization, blog content" },
  { id: "admin_support", label: "Admin & Operations Support", description: "Virtual assistance, systems setup" },
  { id: "media_promo", label: "Media & Podcast Production", description: "Audio/video production, promotion" },
  { id: "consulting", label: "Strategic Consulting", description: "Business strategy, growth planning" },
  { id: "other", label: "Other", description: "Custom project request" }
];

const TIMELINE_OPTIONS = [
  { id: "urgent", label: "ASAP (Within 1 week)", icon: "🔥" },
  { id: "short", label: "Short-term (2-4 weeks)", icon: "⚡" },
  { id: "medium", label: "Medium-term (1-2 months)", icon: "📅" },
  { id: "long", label: "Long-term (3+ months)", icon: "🗓️" },
  { id: "ongoing", label: "Ongoing/Retainer", icon: "🔄" },
  { id: "flexible", label: "Flexible", icon: "✨" }
];

const BUDGET_OPTIONS = [
  { id: "under_500", label: "Under $500" },
  { id: "500_1000", label: "$500 - $1,000" },
  { id: "1000_2500", label: "$1,000 - $2,500" },
  { id: "2500_5000", label: "$2,500 - $5,000" },
  { id: "5000_10000", label: "$5,000 - $10,000" },
  { id: "10000_plus", label: "$10,000+" },
  { id: "discuss", label: "To be discussed" }
];

export const ProjectScopeRequest = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    // Step 1: Contact Info
    name: "",
    email: "",
    phone: "",
    company_name: "",
    website: "",
    
    // Step 2: Project Details
    project_types: [],
    project_title: "",
    project_description: "",
    goals: "",
    
    // Step 3: Timeline & Budget
    timeline: "",
    budget_range: "",
    start_date: "",
    
    // Step 4: Additional Info
    has_existing_materials: false,
    existing_materials_description: "",
    competitors: "",
    additional_notes: "",
    how_heard: ""
  });

  const totalSteps = 4;
  const progress = (step / totalSteps) * 100;

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleProjectType = (typeId) => {
    setFormData(prev => ({
      ...prev,
      project_types: prev.project_types.includes(typeId)
        ? prev.project_types.filter(t => t !== typeId)
        : [...prev.project_types, typeId]
    }));
  };

  const validateStep = () => {
    switch (step) {
      case 1:
        if (!formData.name.trim()) {
          toast.error("Please enter your name");
          return false;
        }
        if (!formData.email.trim() || !formData.email.includes("@")) {
          toast.error("Please enter a valid email address");
          return false;
        }
        return true;
      case 2:
        if (formData.project_types.length === 0) {
          toast.error("Please select at least one project type");
          return false;
        }
        if (!formData.project_description.trim()) {
          toast.error("Please describe your project");
          return false;
        }
        return true;
      case 3:
        if (!formData.timeline) {
          toast.error("Please select a timeline");
          return false;
        }
        if (!formData.budget_range) {
          toast.error("Please select a budget range");
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep()) {
      setStep(prev => Math.min(prev + 1, totalSteps));
    }
  };

  const handleBack = () => {
    setStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const response = await fetch(`${API}/project-scope-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSubmitted(true);
        toast.success("Project scope request submitted successfully!");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to submit request");
      }
    } catch (error) {
      toast.error("An error occurred. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <Card className="border-green-200 bg-white shadow-xl">
            <CardContent className="p-12 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-3xl font-bold text-neutral-900 mb-4">
                Request Submitted!
              </h2>
              <p className="text-lg text-neutral-600 mb-6">
                Thank you for your project scope request. Our team will review your submission 
                and get back to you within 24-48 hours with next steps.
              </p>
              <div className="bg-purple-50 rounded-lg p-4 mb-8">
                <p className="text-sm text-purple-700">
                  <strong>What happens next?</strong><br />
                  A consultant will review your request and reach out to schedule a discovery call 
                  to discuss your project in detail.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button onClick={() => navigate("/pricing")} variant="outline">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Pricing
                </Button>
                <Button onClick={() => navigate("/clarity-assessment")} className="bg-[#39FF14] hover:bg-[#32E010] text-black">
                  Take Business Assessment
                  <Sparkles className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/pricing" className="inline-flex items-center text-purple-600 hover:text-purple-800 mb-4">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Pricing
          </Link>
          <h1 className="text-3xl sm:text-4xl font-bold text-neutral-900 mb-2">
            Request a Project Scope
          </h1>
          <p className="text-lg text-neutral-600">
            Tell us about your project and we'll provide a custom proposal
          </p>
        </div>

        {/* Progress */}
        <div className="mb-8">
          <div className="flex justify-between text-sm text-neutral-500 mb-2">
            <span>Step {step} of {totalSteps}</span>
            <span>{Math.round(progress)}% complete</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Form Card */}
        <Card className="shadow-xl border-0">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2">
              {step === 1 && <><Users className="w-5 h-5 text-purple-600" /> Contact Information</>}
              {step === 2 && <><Briefcase className="w-5 h-5 text-purple-600" /> Project Details</>}
              {step === 3 && <><Calendar className="w-5 h-5 text-purple-600" /> Timeline & Budget</>}
              {step === 4 && <><FileText className="w-5 h-5 text-purple-600" /> Additional Information</>}
            </CardTitle>
            <CardDescription>
              {step === 1 && "How can we reach you?"}
              {step === 2 && "Tell us about your project needs"}
              {step === 3 && "Help us understand your timeline and investment"}
              {step === 4 && "Any additional context that would be helpful"}
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Step 1: Contact Info */}
            {step === 1 && (
              <>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      placeholder="Your full name"
                      value={formData.name}
                      onChange={(e) => updateField("name", e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={(e) => updateField("email", e.target.value)}
                    />
                  </div>
                </div>
                
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      placeholder="(555) 123-4567"
                      value={formData.phone}
                      onChange={(e) => updateField("phone", e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company">Company/Business Name</Label>
                    <Input
                      id="company"
                      placeholder="Your business name"
                      value={formData.company_name}
                      onChange={(e) => updateField("company_name", e.target.value)}
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="website">Website (if applicable)</Label>
                  <Input
                    id="website"
                    placeholder="https://yourwebsite.com"
                    value={formData.website}
                    onChange={(e) => updateField("website", e.target.value)}
                  />
                </div>
              </>
            )}

            {/* Step 2: Project Details */}
            {step === 2 && (
              <>
                <div className="space-y-3">
                  <Label>Project Type(s) * <span className="text-neutral-400 font-normal">(Select all that apply)</span></Label>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {PROJECT_TYPES.map((type) => (
                      <div
                        key={type.id}
                        onClick={() => toggleProjectType(type.id)}
                        className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          formData.project_types.includes(type.id)
                            ? "border-purple-500 bg-purple-50"
                            : "border-neutral-200 hover:border-purple-300"
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <Checkbox
                            checked={formData.project_types.includes(type.id)}
                            className="mt-0.5"
                          />
                          <div>
                            <p className="font-medium text-neutral-900">{type.label}</p>
                            <p className="text-sm text-neutral-500">{type.description}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="title">Project Title</Label>
                  <Input
                    id="title"
                    placeholder="Give your project a name"
                    value={formData.project_title}
                    onChange={(e) => updateField("project_title", e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Project Description *</Label>
                  <Textarea
                    id="description"
                    placeholder="Describe what you're looking to accomplish. The more detail, the better we can help..."
                    rows={5}
                    value={formData.project_description}
                    onChange={(e) => updateField("project_description", e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="goals">What does success look like?</Label>
                  <Textarea
                    id="goals"
                    placeholder="What outcomes or results would make this project a success?"
                    rows={3}
                    value={formData.goals}
                    onChange={(e) => updateField("goals", e.target.value)}
                  />
                </div>
              </>
            )}

            {/* Step 3: Timeline & Budget */}
            {step === 3 && (
              <>
                <div className="space-y-3">
                  <Label>Desired Timeline *</Label>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {TIMELINE_OPTIONS.map((option) => (
                      <div
                        key={option.id}
                        onClick={() => updateField("timeline", option.id)}
                        className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          formData.timeline === option.id
                            ? "border-purple-500 bg-purple-50"
                            : "border-neutral-200 hover:border-purple-300"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{option.icon}</span>
                          <span className="font-medium text-neutral-900">{option.label}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-3">
                  <Label>Budget Range *</Label>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {BUDGET_OPTIONS.map((option) => (
                      <div
                        key={option.id}
                        onClick={() => updateField("budget_range", option.id)}
                        className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          formData.budget_range === option.id
                            ? "border-green-500 bg-green-50"
                            : "border-neutral-200 hover:border-green-300"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <DollarSign className={`w-4 h-4 ${formData.budget_range === option.id ? "text-green-600" : "text-neutral-400"}`} />
                          <span className="font-medium text-neutral-900">{option.label}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="start_date">Ideal Start Date</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => updateField("start_date", e.target.value)}
                  />
                </div>
              </>
            )}

            {/* Step 4: Additional Info */}
            {step === 4 && (
              <>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="has_materials"
                      checked={formData.has_existing_materials}
                      onCheckedChange={(checked) => updateField("has_existing_materials", checked)}
                    />
                    <div>
                      <Label htmlFor="has_materials" className="cursor-pointer">
                        I have existing materials to share
                      </Label>
                      <p className="text-sm text-neutral-500">
                        Logos, brand guidelines, previous work, or reference materials
                      </p>
                    </div>
                  </div>
                  
                  {formData.has_existing_materials && (
                    <Textarea
                      placeholder="Describe the materials you have or can provide..."
                      rows={2}
                      value={formData.existing_materials_description}
                      onChange={(e) => updateField("existing_materials_description", e.target.value)}
                      className="mt-2"
                    />
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="competitors">Competitors or Inspiration</Label>
                  <Textarea
                    id="competitors"
                    placeholder="List any competitors or examples of work you admire..."
                    rows={2}
                    value={formData.competitors}
                    onChange={(e) => updateField("competitors", e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Additional Notes</Label>
                  <Textarea
                    id="notes"
                    placeholder="Anything else you'd like us to know?"
                    rows={3}
                    value={formData.additional_notes}
                    onChange={(e) => updateField("additional_notes", e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="how_heard">How did you hear about us?</Label>
                  <Select
                    value={formData.how_heard}
                    onValueChange={(value) => updateField("how_heard", value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select an option" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="google">Google Search</SelectItem>
                      <SelectItem value="social">Social Media</SelectItem>
                      <SelectItem value="referral">Referral/Friend</SelectItem>
                      <SelectItem value="podcast">Podcast</SelectItem>
                      <SelectItem value="event">Event/Workshop</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Summary */}
                <div className="mt-6 p-4 bg-purple-50 rounded-lg">
                  <h4 className="font-semibold text-purple-900 mb-2">Request Summary</h4>
                  <div className="grid sm:grid-cols-2 gap-2 text-sm">
                    <p><strong>Contact:</strong> {formData.name}</p>
                    <p><strong>Email:</strong> {formData.email}</p>
                    <p><strong>Project Types:</strong> {formData.project_types.length} selected</p>
                    <p><strong>Timeline:</strong> {TIMELINE_OPTIONS.find(t => t.id === formData.timeline)?.label || "Not selected"}</p>
                    <p><strong>Budget:</strong> {BUDGET_OPTIONS.find(b => b.id === formData.budget_range)?.label || "Not selected"}</p>
                  </div>
                </div>
              </>
            )}
          </CardContent>

          {/* Navigation */}
          <div className="px-6 pb-6 flex justify-between">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={step === 1}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            
            {step < totalSteps ? (
              <Button onClick={handleNext} className="bg-purple-600 hover:bg-purple-700">
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button 
                onClick={handleSubmit} 
                disabled={submitting}
                className="bg-[#39FF14] hover:bg-[#32E010] text-black"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
                ) : (
                  <><CheckCircle className="w-4 h-4 mr-2" /> Submit Request</>
                )}
              </Button>
            )}
          </div>
        </Card>

        {/* Trust indicators */}
        <div className="mt-8 text-center text-sm text-neutral-500">
          <p>Your information is secure and will only be used to process your request.</p>
          <p className="mt-1">Typical response time: 24-48 hours</p>
        </div>
      </div>
    </div>
  );
};

export default ProjectScopeRequest;
